/**
 * `agentPrompts.ts` — port of Unity's `AI/AgentPrompts.cs` (`AIAgentPrompts`), post
 * progressive-disclosure refactor.
 *
 * The base prompt is data-driven: it loads exclusively from `/AI/prompt/base.md` (via
 * `AIRealtimeBasePromptCatalog.ts`) and there is NO compiled-in fallback — if the file is
 * missing/unreadable the base prompt stays empty and the session is degraded (Unity parity).
 * Roles and personalities are likewise registered from the `/AI/roles` + `/AI/personalities`
 * catalogs; the bootstrap sets below only cover the window before the catalogs load.
 *
 * Keep `public/AI/**` in sync with Unity's `StreamingAssets/AI/**` — the markdown content is the
 * shared behavioral contract between the two clients.
 */

import type {
  AIRealtimePersonalityDefinition,
  AIRealtimeRoleDefinition,
} from "./aiRealtimeContentTypes";

// ---------------------------------------------------------------------------
// Personalities
// ---------------------------------------------------------------------------

export interface AgentPersonality {
  readonly id: string;
  readonly displayName: string;
  readonly additionalInstructions: string;
  readonly appearanceName: string;
  readonly voiceName: string;
}

export const DEFAULT_PERSONALITY_ID = "normal";
export const GUIDE_PERSONALITY_ID = "guide";

// Personalities (voice rules, realtime voice, and Spark appearance) are data-driven from
// /AI/personalities/*.md and registered via setPersonalityDefinitions before the realtime session
// connects. This bootstrap set matches the shipped personalities so personality ids/dropdowns and
// the default voice are never empty before the catalog loads.
function buildBootstrapPersonalities(): AgentPersonality[] {
  return [
    {
      id: DEFAULT_PERSONALITY_ID,
      displayName: "Marin",
      additionalInstructions: "# Voice Rules\n- Speak like you're cheerfull and excited.",
      appearanceName: "Head",
      voiceName: "marin",
    },
    {
      id: GUIDE_PERSONALITY_ID,
      displayName: "Guide",
      additionalInstructions: "# Voice Rules\n- Be polite and professional.",
      appearanceName: "SmartHead",
      voiceName: "ash",
    },
  ];
}

let s_personalities: ReadonlyArray<AgentPersonality> = buildBootstrapPersonalities();

export function getPersonalities(): ReadonlyArray<AgentPersonality> {
  return s_personalities;
}

export function getPersonalityIds(): ReadonlyArray<string> {
  return s_personalities.map((p) => p.id);
}

/**
 * Registers the personalities loaded from `/AI/personalities/*.md`. Called before the realtime
 * session connects; a null/empty list falls back to the bootstrap set. Mirrors
 * `AIAgentPrompts.SetPersonalityDefinitions`.
 */
export function setPersonalityDefinitions(
  personalities: readonly AIRealtimePersonalityDefinition[] | null,
): void {
  const definitions: AgentPersonality[] = [];
  if (personalities) {
    for (const personality of personalities) {
      if (!personality || !personality.id.trim()) {
        continue;
      }

      definitions.push({
        id: personality.id.trim(),
        displayName: personality.displayName,
        additionalInstructions: personality.markdown,
        appearanceName: personality.appearanceName ?? "",
        voiceName: personality.voiceName ?? "",
      });
    }
  }

  s_personalities = definitions.length > 0 ? definitions : buildBootstrapPersonalities();
}

export function getPersonality(personalityId?: string | null): AgentPersonality {
  const fallback =
    s_personalities.find((p) => p.id.toLowerCase() === DEFAULT_PERSONALITY_ID) ??
    s_personalities[0];
  if (!personalityId || !personalityId.trim()) return fallback;
  let id = personalityId.trim().toLowerCase();
  if (id === "trainer") id = GUIDE_PERSONALITY_ID;
  return (
    s_personalities.find((p) => p.id.toLowerCase() === id) ??
    s_personalities.find((p) => p.displayName.toLowerCase() === id) ??
    fallback
  );
}

// ---------------------------------------------------------------------------
// Roles
// ---------------------------------------------------------------------------

export interface AgentRoleDefinition {
  readonly id: string;
  readonly displayName: string;
  /**
   * Frontmatter `description` from `/AI/roles/*.md`. This is AGENT-FACING: it is
   * what `switch_role` lists so the model can tell the roles apart. Before it
   * was carried here the tool restated the same six roles in hand-written prose,
   * so a new or reworded role file silently disagreed with the tool that offers
   * it — the drift `Campfire_AI.md`'s prompt rule exists to prevent. Empty for a
   * role whose file declares none (and for the legacy bootstrap set).
   */
  readonly description: string;
}

// The default project role when nothing is selected/persisted. Only the three roles the app itself
// switches into are named in code; everything else (which roles exist, their display names,
// personas, tools) is data-driven from /AI/roles/*.md via setRoleDefinitions.
export const DEFAULT_ROLE_ID = "assistant";

/**
 * The role a guided workflow run is narrated in. The orchestrator switches the
 * SESSION to it while a run is playing and hands the previous role back when the
 * run stops (`applyWorkflowAgentRole`).
 *
 * A second identity constant in code, and worth the exception: a run has to be
 * narrated by the locked-down role rather than by whichever one the session
 * happens to be in, and "which role owns playback" is a decision about the
 * app, not a property any single role file can declare about itself. A missing
 * or account-withdrawn `guide` is handled by the caller (the run keeps the
 * current role) rather than by falling back to the default, which would demote
 * an author mid-preview.
 */
export const PLAYBACK_ROLE_ID = "guide";

/**
 * The role the Workflow editor is authored in. Opening the panel switches the
 * SESSION to it and closing hands the previous role back
 * (`AIOrchestratorModel.setWorkflowEditorOpen`).
 *
 * A third identity constant, for the same reason as `PLAYBACK_ROLE_ID`: the
 * panel has no prompt bar of its own, so the author talks to the builder
 * through the normal Spark input and the app has to say which role that input
 * lands in. Withdrawn on the account, the caller leaves the session where it is
 * rather than demoting it to the default.
 */
export const WORKFLOW_BUILDER_ROLE_ID = "workflow-builder";

// The three roles the app names in code, standing in until `/AI/roles/*.md` loads.
// Roles are SESSION state — nothing persists an id — so this set exists only to
// keep `switch_role` and the persona lookup answerable during that window.
function buildBootstrapRoleDefinitions(): AgentRoleDefinition[] {
  return [DEFAULT_ROLE_ID, PLAYBACK_ROLE_ID, WORKFLOW_BUILDER_ROLE_ID].map((id) => ({
    id,
    displayName: id.length > 0 ? id[0].toUpperCase() + id.substring(1) : id,
    // The bootstrap set exists only until the catalog loads; it has no file to
    // take a description from, so `switch_role` lists bare ids in that window.
    description: "",
  }));
}

let s_roleDefinitions: ReadonlyArray<AgentRoleDefinition> = buildBootstrapRoleDefinitions();
let s_rolePersonas: ReadonlyMap<string, string> | null = null;

export function getRoleDefinitions(): ReadonlyArray<AgentRoleDefinition> {
  return s_roleDefinitions;
}

export function getRoleIds(): ReadonlyArray<string> {
  return s_roleDefinitions.map((role) => role.id);
}

function findRoleDefinition(roleId: string | null | undefined): AgentRoleDefinition | null {
  if (!roleId || !roleId.trim()) {
    return null;
  }

  const target = roleId.trim().toLowerCase();
  return s_roleDefinitions.find((definition) => definition.id.toLowerCase() === target) ?? null;
}

export function getRoleDisplayName(roleId: string): string {
  return findRoleDefinition(roleId)?.displayName ?? roleId;
}

/**
 * Resolves an input (role id or display name) to a canonical, currently-known role id. Returns
 * `null` when the input matches no known role (Unity's `TryResolveRoleId` false branch — callers
 * fall back to `DEFAULT_ROLE_ID`).
 */
export function tryResolveRoleId(input: string | null | undefined): string | null {
  if (input && input.trim()) {
    const byId = findRoleDefinition(input);
    if (byId) {
      return byId.id;
    }

    const target = input.trim().toLowerCase();
    const byName = s_roleDefinitions.find(
      (definition) => definition.displayName.toLowerCase() === target,
    );
    if (byName) {
      return byName.id;
    }
  }

  return null;
}

/**
 * Registers the role definitions + personas loaded from `/AI/roles/*.md`. Called once the role
 * catalog finishes loading; a null/empty list falls back to the bootstrap set. Mirrors
 * `AIAgentPrompts.SetRoleDefinitions`.
 */
export function setRoleDefinitions(roles: readonly AIRealtimeRoleDefinition[] | null): void {
  const personas = new Map<string, string>();
  const definitions: AgentRoleDefinition[] = [];
  if (roles) {
    for (const role of roles) {
      if (!role || !role.id.trim()) {
        continue;
      }

      const id = role.id.trim();
      definitions.push({
        id,
        displayName: role.displayName.trim() ? role.displayName.trim() : id,
        description: role.description?.trim() ?? "",
      });
      if (role.markdown.trim()) {
        personas.set(id.toLowerCase(), role.markdown.trim());
      }
    }
  }

  s_rolePersonas = personas;
  s_roleDefinitions = definitions.length > 0 ? definitions : buildBootstrapRoleDefinitions();
}

// Returns the active role's persona body (injected into the base prompt's "# Role" section), or
// null when the role catalog has not loaded a persona for this role yet.
function getRolePersonaBody(roleId: string | null | undefined): string | null {
  if (!roleId || !roleId.trim() || !s_rolePersonas) {
    return null;
  }

  const persona = s_rolePersonas.get(roleId.trim().toLowerCase());
  return persona && persona.trim() ? persona.trim() : null;
}

// ---------------------------------------------------------------------------
// Base prompt + assembly
// ---------------------------------------------------------------------------

const K_PERSONALITY_VOICE_PLACEHOLDER = "{personality_voice}";
const K_ROLE_PLACEHOLDER = "{role}";

// The base prompt is loaded exclusively from /AI/prompt/base.md via AIRealtimeBasePromptCatalog,
// before the realtime session connects. There is no compiled-in fallback; if the file is
// missing/unreadable this stays empty.
let s_baseInstructions = "";

/**
 * The fixed refusal, verbatim from `prompt/base.md` § Safety and Guardrails.
 * `skills/answer.md` restates the same string for the Answer skill.
 *
 * ⚠ THREE COPIES, and TypeScript can only see this one. The markdown is what the
 * MODEL reads; this constant is what the panel matches its reply against. An edit
 * to base.md that does not land here does not fail a build, a typecheck or a lint
 * — the panel simply stops recognising a refusal and every guard keyed on it goes
 * quiet. {@link setBaseInstructionsOverride} is the only place that can notice, so
 * it checks the loaded prompt still contains this string and warns when it does not.
 */
export const K_REFUSAL_ANSWER = "Sorry, I do not have this information";

/**
 * Whether an assistant reply IS the fixed refusal.
 *
 * Tolerant of case, internal whitespace and a trailing period only — base.md
 * forbids paraphrasing, translating, softening or padding the string, so a looser
 * test (a prefix, or "starts with sorry") would start matching real answers that
 * merely open apologetically and strip their sources.
 */
export function isRefusalAnswer(text: string): boolean {
  const normalized = text
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ")
    .replace(/[.!]+$/, "");
  return normalized === K_REFUSAL_ANSWER.toLowerCase();
}

/**
 * Registers the base prompt loaded from `/AI/prompt/base.md`. Loaded before the realtime session
 * connects. If null/empty, the base prompt will be empty (no compiled-in fallback).
 */
export function setBaseInstructionsOverride(baseInstructions: string | null): void {
  s_baseInstructions = baseInstructions && baseInstructions.trim() ? baseInstructions : "";
  // The one moment the markdown and {@link K_REFUSAL_ANSWER} are both in hand.
  // A published prompt override reaches here too, so an edited prompt that drops
  // or reworks the refusal is caught on the deployment that published it.
  if (s_baseInstructions && !s_baseInstructions.includes(K_REFUSAL_ANSWER)) {
    console.warn(
      `[agentPrompts] the loaded base prompt does not contain the refusal string ` +
        `"${K_REFUSAL_ANSWER}" — anything matching on it (the panel's source guard) ` +
        `will no longer recognise a refusal. Update K_REFUSAL_ANSWER to match base.md.`,
    );
  }
}

/** Port of `AIAgentPrompts.GetSystemInstructions(roleId, personalityId)`. */
export function getSystemInstructions(roleId: string, personalityId?: string | null): string {
  const personality = getPersonality(personalityId);
  const personalityVoice = personality?.additionalInstructions.trim() ?? "";
  const rolePersona = getRolePersonaBody(roleId) ?? "";

  return s_baseInstructions
    .replaceAll(K_PERSONALITY_VOICE_PLACEHOLDER, personalityVoice)
    .replaceAll(K_ROLE_PLACEHOLDER, rolePersona);
}

/**
 * Port of `AIAgentPrompts.GetMainAgentInstructions`.
 *
 * Section order is contractual (matches Unity): system instructions, `# User`,
 * `# Project-Specific Instructions`, `# Runtime Conversation Instructions`, the available-skills
 * index, then the loaded skills. Available-skills MUST precede loaded-skills.
 */
export function getMainAgentInstructions(
  roleId: string,
  personalityId: string | null,
  projectPrompt?: string | null,
  runtimePrompt?: string | null,
  userFirstName?: string | null,
  loadedSkillsPrompt?: string | null,
  availableSkillsPrompt?: string | null,
): string {
  const instructions = getSystemInstructions(roleId, personalityId);
  const project = projectPrompt?.trim() || null;
  const runtime = runtimePrompt?.trim() || null;
  const firstName = userFirstName?.trim() || null;
  const loadedSkills = loadedSkillsPrompt?.trim() || null;
  const availableSkills = availableSkillsPrompt?.trim() || null;
  if (!project && !runtime && !firstName && !loadedSkills && !availableSkills) {
    return instructions;
  }

  const sections = [instructions];
  if (firstName) sections.push(`# User\n- The user's first name is ${firstName}.`);
  if (project) sections.push(`# Project-Specific Instructions\n${project}`);
  if (runtime) sections.push(`# Runtime Conversation Instructions\n${runtime}`);
  if (availableSkills) sections.push(availableSkills);
  if (loadedSkills) sections.push(loadedSkills);
  return sections.join("\n\n");
}
